import type { ValueOf } from "../types"

export const ROLES = {
  ADMIN: "admin",
  DEVELOPER: "developer",
  ENGINEER: "engineer",
  VIEWER: "viewer",
  USER: "user",
}

type Action = "update" | "view" | "delete" | "create"
export type Permission = `${Action}:${string}`

export const ALL_PERMISSIONS = [
  "view:admin_page",
  "view:station_access",
  "view:station_remote_control",
  "view:admin_instructions",
  "view:full_admin_panel",
  "view:all_filters",
  "view:pool_status",
  "view:station_tags",
  "view:emulator",
  "view:org_name",
  "view:alarms",
  "view:targets",
  "view:version",
  "view:map",
  "view:simple_mode",
  "view:org_name",
  "view:map_work_mode",
  "view:map_setup_mode",
  "view:apps_access",
  "view:target_fake",

  "create:developer_users",
  "create:station",
  "create:org",

  "update:station_settings",
  "update:units_modal",
  "update:station_orgs",
  "update:remote_control",
  "update:station_tags",
  "update:groups",
  "update:presets",

  "delete:stations",
  "delete:alarm_for_sdr",
  "delete:targets",
] satisfies Permission[]

export const rolePermissions: Record<
  ValueOf<typeof ROLES>,
  typeof ALL_PERMISSIONS
> = {
  [ROLES.ADMIN]: [
    "view:admin_page",
    "view:station_access",
    "view:map",
    "view:map_work_mode",
    "view:map_setup_mode",

    "update:station_settings",
    "update:groups",

    "delete:targets",
  ],
  [ROLES.DEVELOPER]: [
    "view:admin_page",
    "view:station_access",
    "view:full_admin_panel",
    "view:station_remote_control",
    "view:all_filters",
    "view:station_tags",
    "view:emulator",
    "view:pool_status",
    "view:org_name",
    "view:alarms",
    "view:targets",
    "view:version",
    "view:map",
    "view:simple_mode",
    "view:org_name",
    "view:map_work_mode",
    "view:map_setup_mode",
    "view:apps_access",
    "view:target_fake",

    "update:station_settings",
    "update:remote_control",
    "update:units_modal",
    "update:station_orgs",
    "update:groups",
    "update:station_tags",

    "create:developer_users",
    "create:station",
    "create:org",

    "delete:stations",
    "delete:alarm_for_sdr",
    "delete:targets",
  ],
  [ROLES.ENGINEER]: [
    "view:admin_page",
    "view:station_access",
    "view:map",
    "view:map_setup_mode",
    "view:map_work_mode",

    "update:station_settings",
    "update:groups",

    "delete:targets",
  ],
  [ROLES.VIEWER]: ["view:map"],
  [ROLES.USER]: ["view:map"],
}

export type RoleKey = keyof typeof rolePermissions

const ROLE_LEVEL_TO_NAME: Record<string, RoleKey> = {
  "0": ROLES.VIEWER,
  "1": ROLES.USER,
  "2": ROLES.ENGINEER,
  "3": ROLES.ADMIN,
  "4": ROLES.DEVELOPER,
}

export const normalizePermissionRole = (
  role: string | number | null | undefined,
): RoleKey => {
  if (role === null || role === undefined) return ROLES.VIEWER

  const roleKey = String(role).toLowerCase()
  const normalized = ROLE_LEVEL_TO_NAME[roleKey] ?? roleKey

  return normalized in rolePermissions ? (normalized as RoleKey) : ROLES.VIEWER
}
